// Chart intent — the presentation overlay over the Lib output schema
// (outputs.ts). This is chrome, not contract: per algorithm, how to turn the
// emitted `data[]` into dashboard-kit charts (chart type, which output fields
// map to x / series / measure, colors, Spanish labels). Field names reference
// outputs.schema.json; chartIntent.test.ts drift-guards every reference.
//
// The generic mapper (lib/resultToDashboard.ts) interprets these intents, so
// adding a chart for an algorithm whose shape fits an existing `layout` is a
// declaration here — no per-algorithm mapper code.

import type { ChartConfig } from '@micronet/dashboard-kit';
import { ERROR_BASE_ORDER } from '../types/compute';

type ChartType = ChartConfig['type'];

export type ColorRole = 'lp' | 'cp';

// Blue (cool) vs orange (warm): a neutral, publication-grade pair for the
// client deliverable — deliberately NOT the app brand. Separable by hue AND
// luminance, so the two models stay distinct on screen, in grayscale / printed
// PDF, and for colorblind readers. Shared by the config swatches and the chart
// series. Chart colors are client-facing and brand-independent by design.
//
// CP is a dark burnt orange, not the vivid `#f97316` first picked: that shade's
// relative luminance (0.325, WCAG) sits a 1.11:1 ratio from the reconciliation
// scatter's orange (W2O pérdida, `#ff7f0e`, 0.365; the accuracy maps carried it
// too until Task 2643) — indistinguishable in hue AND in grayscale/print, even
// though the two never share a figure (CP series vs scatter classes), because
// they DO share a report: same PDF, same color meaning two different things. `#9a3412`
// (Tailwind orange-800) drops CP's luminance to 0.094 — 1.41:1 from LP (still
// clearly separate, though less than the ~1.84:1 this pair had before) and
// 2.88:1 from the scatter orange (was 1.11:1) — while staying in-hue with the
// orange the client asked for. Recorded per review on SPEC/2231 (§7); the
// scatter/accuracy palette was left untouched then — see "Fuera de alcance" there.
// Since Task 2643 the maps' Q4 blue (`#0000ff`, client-specified) is near LP's
// blue in hue — same caveat, accepted because the client chose it.
export const LP_COLOR = '#2563eb'; // vivid blue
export const CP_COLOR = '#9a3412'; // dark burnt orange

export const COLOR: Record<ColorRole, string> = { lp: LP_COLOR, cp: CP_COLOR };

// Single-series line color for `accuracy_cutoff` ("Porcentaje de acierto por ley
// de corte"), matching the reference legacy system's rendering of the same
// chart (US 2461). Not an LP/CP role — this chart has one series (the match
// percentage, not a per-model comparison) — so it is its own constant rather
// than a third `ColorRole`. Sampled directly from the reference screenshot's
// line/marker pixels (most frequent non-anti-aliased RGB, decoded losslessly
// from the PNG): a muted teal-green, distinct from both LP/CP and from the
// brighter `#2ca02c` "acierto" green used in the reconciliation scatter palette
// below (that marks a classification class; this is a plain line color).
export const ACCURACY_LINE_COLOR = '#5a9788';

/**
 * Reconciliation classes emitted by the lib's `classificationCutoff` (per
 * cut-off grade): the numeric code carried in `scatterCategory`'s `t[2]`,
 * mapped to a readable label + color. Concordances (1, 4) read calm (teal /
 * grey); discordances (2, 3) read warm-alert (ochre / rust) so the
 * misclassification quadrants pop. CVD-safe — no red-vs-green reliance.
 */
// Accuracy palette for the reconciliation scatter: green = correct (ore↔ore),
// grey = correct (waste↔waste), orange = loss, red = dilution. See point 3. The
// accuracy maps (CUTOFF/CATEGORY_MAP_CLASSES) used to share it; since Task 2643
// they carry their own client-specified colors.
export const RECONCILIATION_CLASSES: Record<number, { label: string; color: string }> = {
  1: { label: 'O2O → CP(O)2LP(O)', color: '#2ca02c' }, // ambos ≥ corte (mineral/mineral) — acierto
  2: { label: 'W2O → CP(W)2LP(O)', color: '#ff7f0e' }, // LP ≥ corte, CP < corte — PÉRDIDA
  3: { label: 'O2W → CP(O)2LP(W)', color: '#d62728' }, // LP < corte, CP ≥ corte — DILUCIÓN
  4: { label: 'W2W → CP(W)2LP(W)', color: '#b0b0b0' }, // ambos < corte (estéril/estéril) — acierto
};

/** Label → color for the reconciliation classes (for a chart's `groupColors`). */
export const RECONCILIATION_GROUP_COLORS: Record<string, string> = Object.fromEntries(
  Object.values(RECONCILIATION_CLASSES).map((c) => [c.label, c.color])
);

/** One class (quadrant) of a 2×2 confusion-matrix map: its readable label + color. */
interface ConfusionClass {
  label: string;
  color: string;
}

/**
 * Derive the four presentation views a plan-view confusion map needs from its
 * class table (Lib codes 1–4). The cut-off map and the category map are the same
 * 2×2 matrix, so both derive identically — this keeps them from drifting apart:
 *   • accuracyClasses — the "correct" quadrants Q1 (code 1) + Q4 (code 4).
 *   • classLabels     — code → label (for the raw Lib code stored under groupKey).
 *   • groupColors     — label → color (for the legend).
 *   • drawOrder       — backdrop → top: waste hit (4) first, errors (2,3), then
 *                       the mineral hit (1) on top, per the mockup's overlay.
 */
function deriveConfusionMap(classes: Record<number, ConfusionClass>) {
  const label = (code: number) => classes[code].label;
  return {
    accuracyClasses: [label(1), label(4)],
    classLabels: Object.fromEntries(
      Object.entries(classes).map(([code, c]) => [Number(code), c.label])
    ) as Record<number, string>,
    groupColors: Object.fromEntries(
      Object.values(classes).map((c) => [c.label, c.color])
    ) as Record<string, string>,
    drawOrder: [label(4), label(2), label(3), label(1)],
    // Reading order, which is NOT the draw order: the map has to put the backdrop
    // class down first or it covers the hits on top of it, while the legend reads as
    // the confusion matrix is named — Q1, Q2, Q3, Q4. Codes 2 and 3 are Q3 and Q2
    // respectively (the Lib's numbering is not the quadrant's), hence the swap.
    legendOrder: [label(1), label(3), label(2), label(4)],
  };
}

/**
 * Chart 1 (cut-off map) is a 2×2 confusion matrix: CP's ore/waste decision at
 * the cut-off vs LP (the truth). Lib class codes (model[0]=LP, model[1]=CP):
 *   1  LP ore   & CP ore   → acierto mineral (Q1)
 *   2  LP ore   & CP waste → PÉRDIDA (false negative): mineral al botadero
 *   3  LP waste & CP ore   → DILUCIÓN (false positive): lastre a planta
 *   4  LP waste & CP waste → acierto lastre (Q4)
 * Colors are the client's specified defaults (Task 2643): red = mineral hit,
 * blue = waste hit, violet = dilution, green = loss — NOT geological domains.
 * Not red/green CVD-safe (Q1 vs Q3); accepted as the client's choice. Assumes
 * the project's model[0]=LP (truth) convention; if the models are configured
 * CP-first, 2 and 3 swap.
 */
export const CUTOFF_MAP_CLASSES: Record<number, { label: string; color: string }> = {
  1: { label: 'Q1 acierto mineral', color: '#ff0000' }, // 255,0,0 (Task 2643)
  2: { label: 'Q3 pérdida', color: '#00ad00' }, // 0,173,0
  3: { label: 'Q2 dilución', color: '#8000ff' }, // 128,0,255
  4: { label: 'Q4 acierto lastre', color: '#0000ff' }, // 0,0,255
};
const _cutoffMap = deriveConfusionMap(CUTOFF_MAP_CLASSES);
/** The "correct" quadrants (Q1+Q4) — accuracy = their share of the bench. */
export const CUTOFF_MAP_ACCURACY_CLASSES = _cutoffMap.accuracyClasses;
export const CUTOFF_MAP_CLASS_LABELS = _cutoffMap.classLabels;
export const CUTOFF_MAP_GROUP_COLORS = _cutoffMap.groupColors;
/** Draw order (background → top): lastre backdrop, errors, mineral hit on top. */
export const CUTOFF_MAP_DRAW_ORDER = _cutoffMap.drawOrder;
/** Legend reading order — Q1..Q4, independent of the draw order above. */
export const CUTOFF_MAP_LEGEND_ORDER = _cutoffMap.legendOrder;

/**
 * The category-accuracy map (`classificationCategorical`) is the SAME 2×2
 * confusion matrix as the cut-off map, but the positive class is "block is in
 * the checked category" instead of "ore". Lib codes (model[0]=LP truth,
 * model[1]=CP prediction):
 *   1  LP in  & CP in   → acierto (ambos en la categoría) — Q1
 *   2  LP in  & CP out  → CP no la asigna, la verdad sí → falso negativo — Q3
 *   3  LP out & CP in   → CP la asigna, la verdad no    → falso positivo — Q2
 *   4  LP out & CP out  → acierto (ambos fuera)          — Q4
 * Same colors/accuracy as the cut-off map (red/blue hits, violet/green errors).
 */
export const CATEGORY_MAP_CLASSES: Record<number, { label: string; color: string }> = {
  1: { label: 'Q1 acierto (en cat.)', color: '#ff0000' },
  2: { label: 'Q3 falso neg.', color: '#00ad00' },
  3: { label: 'Q2 falso pos.', color: '#8000ff' },
  4: { label: 'Q4 acierto (fuera)', color: '#0000ff' },
};
const _categoryMap = deriveConfusionMap(CATEGORY_MAP_CLASSES);
export const CATEGORY_MAP_ACCURACY_CLASSES = _categoryMap.accuracyClasses;
export const CATEGORY_MAP_CLASS_LABELS = _categoryMap.classLabels;
export const CATEGORY_MAP_GROUP_COLORS = _categoryMap.groupColors;
export const CATEGORY_MAP_DRAW_ORDER = _categoryMap.drawOrder;
export const CATEGORY_MAP_LEGEND_ORDER = _categoryMap.legendOrder;

type ChartOptions = ChartConfig['options'];

/**
 * Chart types the user may switch a chart to in the presentation editor, grouped
 * by data-shape compatibility with the current type. Consumer policy: a swap is
 * offered only between types that consume the same encoding, so changing type
 * never breaks the (fixed) algorithm output. Unknown types fall back to no swap.
 */
const COMPATIBLE_TYPES: Partial<Record<ChartType, ChartType[]>> = {
  line: ['line', 'bar', 'distribution'],
  bar: ['bar', 'line', 'distribution'],
  distribution: ['distribution', 'line', 'bar'],
  scatter: ['scatter', 'histogram2d'],
  histogram2d: ['histogram2d', 'scatter'],
  spatialCategorical: ['spatialCategorical', 'scatter', 'histogram2d'],
  pie: ['pie', 'bar'],
  box: ['box', 'bar'],
};

// Per-chart swap allow-list, keyed by the chart's idSuffix. Wins over the
// type-based table so a specific chart can offer a different set — the cut-off
// tonnage curve offers the bar+line combo and drops "distribution".
const SWAP_OVERRIDE: Record<string, ChartType[]> = {
  'tonnage-grade': ['line', 'bar', 'lineBar'],
};

export function compatibleTypesFor(chart: ChartConfig): ChartType[] {
  // Stacked panels give each measure its own Y axis; line/bar/lineBar plot every
  // series on one shared axis, so a 3-measure chart (grade % vs tonnage Mt)
  // would flatten to unreadable. It has no sensible flat swap — keep it fixed.
  // (The tonnage idSuffix is shared with the dual-axis `line` rendering below.)
  if (chart.type === 'stackedPanels') return [chart.type];
  // Match the idSuffix even when a `-N` suffix is appended — resultToDashboard keeps
  // one as a collision net. Anchored at the END, which is also why a split figure's
  // category goes BEFORE the suffix in its id and not after: everything that
  // prefixes a chart id has to be invisible here, whether it is a minted
  // `name#<uuid>` with its hyphens, the `name#<integer>` a pre-minting config
  // materialises, or a category read from the client's data.
  const hit = Object.keys(SWAP_OVERRIDE).find((s) =>
    new RegExp(`-${s}(-\\d+)?$`).test(chart.id ?? '')
  );
  return hit ? SWAP_OVERRIDE[hit] : (COMPATIBLE_TYPES[chart.type] ?? [chart.type]);
}

/**
 * The canonical figure slot of an `error` chart — its position in
 * ERROR_BASE_ORDER (lp=0, cp=1, sp=2) — or undefined for any other chart.
 *
 * `error_bases` is presentation-only (instanceStaleness's IGNORED_KEYS), so
 * unticking a base redraws the instance with NO re-execution. Numbered by
 * position among the bases actually drawn, unticking CP would slide MSRE into the
 * index a report block captioned about RSE_CP points at — another metric, with a
 * different denominator and the opposite sign, in a client deliverable. Numbered
 * by base, that block resolves to nothing and the editor says so (lib/reportBlocks).
 *
 * Read out of the chart id because that is the only identity a built ChartConfig
 * carries: the intent's idSuffix is not copied onto it. Same lookup shape as
 * compatibleTypesFor, including the `-N` suffix resultToDashboard appends to the
 * 2nd+ envelope of one instance (a fan-out). chartIntent.test.ts pins the ids this
 * matches — minted prefix included — so renaming an idSuffix fails there instead
 * of silently reverting the report to positional numbering.
 */
export function errorBaseSlot(chartId: string): number | undefined {
  const slot = ERROR_BASE_ORDER.findIndex((base) =>
    new RegExp(`-error-${base}(-\\d+)?$`).test(chartId)
  );
  return slot === -1 ? undefined : slot;
}

interface SeriesIntent {
  /** Output field carrying this series' values. */
  field: string;
  label: string;
  color: ColorRole;
}

/**
 * `paired-arrays`: data[0] is one row carrying parallel arrays on a shared x
 * array (e.g. histogram: counts1/counts2 over xAxis). One chart, one series
 * per array, optional statistics pulled from Stats-object fields.
 */
export interface PairedArraysChart {
  layout: 'paired-arrays';
  idSuffix: string;
  type: ChartType;
  title: string;
  subtitle?: string;
  /** Array field providing x values. */
  xField: string;
  /** Fallback array field if `xField` is absent. */
  xFallback?: string;
  /** Key for x in the emitted data points. */
  xKey: string;
  xAxisLabel?: string;
  yAxisLabel?: string;
  series: SeriesIntent[];
  /** Config params [LP, CP] naming the compared columns → resolved into the
   *  subtitle ("Variables: <cp> (CP) · <lp> (LP)"). Absent → the static
   *  `subtitle` is used. */
  comparedColumns?: [string, string];
  /** Stats-object fields → a `μ=… σ=… (n=…)` statistics row. */
  stats?: { field: string; label: string }[];
  options?: ChartOptions;
}

/**
 * One measure plotted in a `models-dual-axis` chart: which field carries its
 * per-position vector, how to name it in the legend (combined with each model
 * label), its axis title, and its line trace. The two measures of a chart sit
 * on distinct Y axes (primary → left, secondary → right).
 */
interface MeasureIntent {
  /** Field on each model row whose vector is plotted for this measure. */
  field: string;
  /** Legend fragment, combined with the model label (e.g. "Tonelaje" → "LP Tonelaje"). */
  label: string;
  /** Title for this measure's Y axis. */
  axisLabel: string;
  /** Line dash style — encodes the measure (mirrors dashboard-kit's `DataSeries['dash']`). */
  dash: 'solid' | 'dash' | 'dot' | 'dashdot';
}

/**
 * `models-dual-axis`: data[] rows are the compared models (LP, CP, …), each
 * carrying two measure vectors indexed by position (e.g. tonnage + grade by
 * cutoff). One chart, two measures on distinct Y axes — primary on the left,
 * secondary on the right. Encoding: color = model, trace (dash) = measure,
 * axis = measure → one series per (model × measure).
 *
 * When 3+ measures are selected, `buildChart` routes this same intent through
 * `buildStackedPanels` instead (one panel per measure, `stackedPanels` chart
 * type) — there the encoding flips: color = panel/measure (so each panel
 * reads as a distinct color, US 2446), dash = model. `models[].color` below
 * is read only by the 2-measure dual-axis path; the stacked-panels path only
 * reads `models[].label`.
 */
export interface ModelsDualAxisChart {
  layout: 'models-dual-axis';
  idSuffix: string;
  type: ChartType;
  title: string;
  /** Key for x in the emitted data points. */
  xKey: string;
  /**
   * Optional config-parameter name whose numeric array supplies the real x
   * values (e.g. the entered cut-off grades). Falls back to the 1-based
   * position when absent or its length is shorter than the measure vectors.
   */
  xParam?: string;
  /** Plot the points in ascending `xParam` order instead of entry order (Bug
   *  2801) — see `IndexedVectorsChart.sortByX`. Only the by-cutoff curve sets
   *  it; the by-categories variant keeps the entered category order. */
  sortByX?: boolean;
  xAxisLabel?: string;
  /** Left (primary) Y-axis measure. */
  primary: MeasureIntent;
  /** Right (secondary) Y-axis measure. */
  secondary: MeasureIntent;
  /**
   * One per model, by row index. `color` only drives the 2-measure dual-axis
   * rendering (`buildModelsDualAxis`) — the 3-measure stacked-panels
   * rendering (`buildStackedPanels`) colors by panel/measure instead and
   * never reads it. See the interface doc above.
   */
  models: { label: string; color: ColorRole }[];
  options?: ChartOptions;
}

/**
 * `xy-points`: data[0] carries a tuple[] of `[x, y, …]` → one scatter chart
 * (e.g. scatter's `scatterCategory`).
 */
export interface XYPointsChart {
  layout: 'xy-points';
  idSuffix: string;
  type: ChartType;
  title: string;
  subtitle?: string;
  /** Tuple[] field; each entry is `[x, y, …]`. */
  field: string;
  xKey: string;
  yKey: string;
  /**
   * Optional: tuple position `t[2]` is a category. When set, it lands on this
   * key in each point and drives `encoding.groupKey` so the chart colors points
   * by category (e.g. the scatter's reconciliation class). Its numeric code is
   * mapped to a readable label via `RECONCILIATION_CLASSES`.
   */
  classKey?: string;
  /** Category → color, paired with `classKey`. */
  groupColors?: Record<string, string>;
  xAxisLabel?: string;
  yAxisLabel?: string;
  options?: ChartOptions;
}

/**
 * `spatial-map`: data[0] carries a map `{"i,j,k": class}` → spatialCategorical
 * points split by class. When the reader grid resolves, the integer indices
 * become real centroids (x=Este, y=Norte, level=elevation); otherwise they stay
 * as grid indices. The level (k) is kept as a field so a level selector can
 * filter to one bench via the kit's `filters`.
 */
export interface SpatialMapChart {
  layout: 'spatial-map';
  idSuffix: string;
  type: ChartType;
  title: string;
  subtitle?: string;
  /** Map field keyed `"i,j,k"` → class. */
  field: string;
  xKey: string;
  yKey: string;
  levelKey: string;
  groupKey: string;
  /** When set, the raw integer class code (from the Lib) stored under `groupKey`
   *  is mapped to a readable label — the 2×2 confusion-matrix quadrants Q1–Q4
   *  (CUTOFF_MAP_CLASS_LABELS for the cut-off map, CATEGORY_MAP_CLASS_LABELS for
   *  the category map). Absent → the raw code is shown. */
  classLabels?: Record<number, string>;
  /** Label → color for the legend, paired with `classLabels`. */
  groupColors?: Record<string, string>;
  xAxisLabel?: string;
  yAxisLabel?: string;
  options?: ChartOptions;
}

/**
 * `indexed-vectors`: data[0] carries one or more equal-length vectors plotted
 * over their 1-based index → one series per vector (e.g. error by category).
 * Colors are left to the palette (these series aren't LP/CP).
 */
export interface IndexedVectorsChart {
  layout: 'indexed-vectors';
  idSuffix: string;
  type: ChartType;
  title: string;
  subtitle?: string;
  xKey: string;
  xAxisLabel?: string;
  yAxisLabel?: string;
  /**
   * Config parameter carrying the real x values (e.g. the cut-off list), same
   * length as the plotted vectors. When set and resolvable, the x axis uses
   * those values instead of the 1-based index; otherwise it falls back to index.
   */
  xParam?: string;
  /**
   * Plot the points in ascending order of their real `xParam` value instead of
   * entry order, so a line never backtracks over a cut-off list typed out of
   * order (Bug 2801). Opt-in: categorical X (match_geology) and the `error`
   * charts' accumulative mode are ordered by entry on purpose.
   */
  sortByX?: boolean;
  /** Config params [LP, CP] naming the compared columns → resolved into the
   *  subtitle ("Variables: <cp> (CP) · <lp> (LP)"). Absent → the static
   *  `subtitle` is used. */
  comparedColumns?: [string, string];
  /** Config params [LP, CP] naming the grouping (category) column the vectors
   *  are indexed by → resolved into the X-axis label (its values are the ticks).
   *  Absent or unresolvable → the static `xAxisLabel` is used. */
  groupColumn?: [string, string];
  /**
   * Appended to the resolved subtitle, after the compared variables. For stating
   * something about the FIGURE itself (e.g. how the number was aggregated) that a
   * reader of a printed report cannot infer from the axes.
   */
  subtitleNote?: string;
  /** Draw the entry's `error_tolerance` (percent) as two dashed black lines at
   *  ±t — see resultToDashboard's toleranceLimits (SPEC/2423, SPEC/2620).
   *  Absent → no lines, whatever the entry carries. */
  toleranceLines?: boolean;
  series: {
    field: string;
    label: string;
    /** Line/marker color, as a raw hex — unlike `PairedArraysChart`, these series
     *  aren't an LP/CP comparison, so there's no `ColorRole` to key off. Absent →
     *  dashboard-kit's default categorical color (the three `error` charts and
     *  `match_geology`). */
    color?: string;
    /** Draw a marker at every plotted point (see `DataSeries.markers`). Absent →
     *  line only, no markers (`match_geology`). */
    markers?: boolean;
    /** Draw a smoothed spline curve instead of straight segments (see
     *  `DataSeries.smooth`). Absent → straight segments (`match_geology`). */
    smooth?: boolean;
    /**
     * HACK(lib-error-scale) companion: name of a boolean config param that,
     * when true, means the Lib already returned THIS series in percent from its
     * second point on, so `valueScale` must not be applied there again.
     *
     * Only the accumulative grade series needs it: `AlgoritmoError::finalizarProceso`
     * pushes the first category untouched and then recomputes the accumulated
     * grade as `100.0 * metalAcc / tonnageAcc`, so one single series carries two
     * scales. Tonnage and metal accumulate raw fractions throughout and keep
     * being scaled.
     *
     * The option that produces those series is withdrawn from the form
     * (HACK(lib-2165) in catalogOverlay + buildBatchRequest), so this only applies
     * to results ALREADY executed with it — which is exactly why it must stay.
     * Retires with Bug 2165, NOT with `valueScale`; see the note on `valueScale`.
     */
    preScaledWhen?: string;
  }[];
  options?: ChartOptions;
}

export type ChartIntent =
  | PairedArraysChart
  | ModelsDualAxisChart
  | XYPointsChart
  | SpatialMapChart
  | IndexedVectorsChart;

/**
 * Point-marker size of the tonnage chart, and of every chart asked to draw its
 * dots "like the tonnage chart" (US 2510's error charts). One value, so they
 * can't drift apart when one is resized. See the note at its use in
 * `tonnageCharts` for why it is smaller than the kit default.
 */
const TONNAGE_MARKER_SIZE = 5;

// Tonnage and grade per cut-off, both models (LP/CP), on one dual-axis chart:
// tonnage on the left axis, grade on the right (dashed). Color = model.
// Both tonnage algorithms share this dual-axis shape; only the X differs — the
// by-cutoff variant plots over the entered cut-off grades, the by-categories
// variant over the chosen categories (`unique_categories`). Same output fields.
const tonnageCharts = (byCategory = false): ModelsDualAxisChart[] => [
  {
    layout: 'models-dual-axis',
    idSuffix: 'tonnage-grade',
    // by-categories draws line+bar (primary measure as grouped bars, secondary
    // as a line on the right axis); by-cutoff stays a line curve.
    //
    // Not for lack of the geometry: `buildModelsDualAxis` emits the per-series
    // `type` both variants would need, and only `lineBar` reads it. It is the X
    // axis that differs. Categories are discrete and evenly spaced, so bars sit
    // in equal slots; cut-off grades are numeric and often unevenly spaced, and
    // Plotly sizes every bar from the SMALLEST gap — a 0.2/0.25/0.5/1.0/2.0 list
    // measured 4px-wide bars in a 428px figure. A curve reads that axis honestly.
    type: byCategory ? 'lineBar' : 'line',
    title: byCategory ? 'Tonelaje y ley por categoría' : 'Tonelaje y ley por ley de corte',
    xKey: byCategory ? 'categoria' : 'cutoff',
    xParam: byCategory ? 'unique_categories' : 'cutoffs',
    sortByX: !byCategory,
    xAxisLabel: byCategory ? 'Categoría' : 'Ley de corte',
    primary: { field: 'totalTonnages', label: 'Tonelaje', axisLabel: 'Tonelaje', dash: 'solid' },
    secondary: { field: 'totalGrades', label: 'Ley', axisLabel: 'Ley', dash: 'dash' },
    models: [
      { label: 'LP', color: 'lp' },
      { label: 'CP', color: 'cp' },
    ],
    // Tonnage (left axis) keeps its 0 floor; grade (right axis, "Ley media (%)")
    // fits to the data instead — small grade values would otherwise sit on a
    // baseline that never reflected their own scale (US 2231).
    options: {
      showLegend: true,
      showGrid: true,
      showTooltip: true,
      y2FitData: true,
      showVerticalGrid: true,
      // Smaller than the kit default (8px) — at these figure sizes a
      // full-size dot crowds the curve, especially where points sit close
      // together (US 2446 follow-up). Shared by both variants: the
      // dual-axis LP/CP markers and, when 3 measures route to
      // `stackedPanels`, its per-point markers too (same `options` object).
      // The error charts reuse it (US 2510).
      markerSize: TONNAGE_MARKER_SIZE,
      ...(byCategory ? { xAxisCategorical: true } : {}),
    },
  },
];

/**
 * How the error figures aggregate, stated on the figure itself. Shared by the
 * three error charts so the three can never say it differently.
 */
const NOTA_METODO_ERROR = 'error sobre agregados por categoría';

/**
 * How the error curves are drawn, shared by the three error charts' nine series:
 * a smoothed spline with a marker at every category, like the "Tonelaje, ley y
 * metal por categoría" stacked panels (US 2510). Every series is marked — unlike
 * those panels, which mark only LP, there is no LP/CP twin here: each series is
 * its own measure.
 */
const ERROR_CURVE = { markers: true, smooth: true } as const;

export const CHART_INTENT: Record<string, ChartIntent[]> = {
  histogram: [
    {
      layout: 'paired-arrays',
      idSuffix: 'histogram',
      type: 'bar',
      title: 'Distribución de leyes (CP vs LP)',
      subtitle: 'Distribución comparada LP vs CP',
      comparedColumns: ['grade_columns_index_1', 'grade_columns_index_2'],
      xField: 'xAxis',
      xFallback: 'edges1',
      xKey: 'bin',
      xAxisLabel: 'Ley (%)',
      // La Lib normaliza counts1/counts2 (c/total) ⇒ frecuencia relativa, no conteo.
      yAxisLabel: 'Frecuencia relativa',
      series: [
        { field: 'counts1', label: 'LP', color: 'lp' },
        { field: 'counts2', label: 'CP', color: 'cp' },
      ],
      stats: [
        { field: 'statsModel1', label: 'LP' },
        { field: 'statsModel2', label: 'CP' },
      ],
      // Grouped bars per bin (correct), LP vs CP; per-model stats box on the right,
      // LP and CP side by side (statsBoxColumns) since they're parallel blocks.
      options: {
        showLegend: true,
        showGrid: true,
        showTooltip: true,
        showStatsBox: true,
        statsBoxRight: true,
        statsBoxColumns: true,
      },
    },
  ],
  tonnage_grade_metal_by_cutoff: tonnageCharts(),
  tonnage_grade_metal_by_categories: tonnageCharts(true),
  scatter: [
    {
      layout: 'xy-points',
      idSuffix: 'scatter',
      type: 'scatter',
      title: 'Dispersión de leyes (CP vs LP)',
      field: 'scatterCategory',
      xKey: 'x',
      yKey: 'y',
      // t[2] is the reconciliation class (depends on the cut-off) → color points by it.
      classKey: 'class',
      groupColors: RECONCILIATION_GROUP_COLORS,
      // Axis labels (variable names), subtitle and statistics are resolved per
      // result in the mapper (buildXYPoints) from the algorithm's columns/params.
      options: {
        showGrid: true,
        showTooltip: true,
        showReferenceLine: true, // surface the 1:1 + trend lines in the legend
        squareAxes: true, // share origin (0) + 1:1 scale
        markerSize: 4,
        showStatsBox: true, // N / correlation / regression / % per class
        statsBoxRight: true, // stats box outside the plot, on the right
        colSpan: 2, // full-width card to fit the plot + side stats box
        aspectRatio: 2.4, // wide card → keep the width but lower the height
      },
    },
  ],
  map_accuracy_cutoff: [
    {
      layout: 'spatial-map',
      idSuffix: 'map-cutoff',
      type: 'spatialCategorical',
      title: 'Mapa de exactitud CP vs LP (ley de corte)',
      field: 'mapCutOff',
      // i/j/k are grid indices; the builder turns them into real centroids
      // (i→Este/X, j→Norte/Y, k→elevation/Z) when the reader grid resolves,
      // and falls back to i/j labels otherwise.
      xKey: 'i',
      yKey: 'j',
      levelKey: 'k',
      groupKey: 'class',
      classLabels: CUTOFF_MAP_CLASS_LABELS,
      groupColors: CUTOFF_MAP_GROUP_COLORS,
      xAxisLabel: 'Este (m)',
      yAxisLabel: 'Norte (m)',
      options: {
        showLegend: true,
        showTooltip: true,
        equalAspect: true, // 1:1 plan view, tight bounds, plain-number axes
        levelSelector: true,
        levelSelectorLabel: 'Elevación (m)',
        levelSelectorUnit: 'm', // real centroid coord; dropped to index if grid fails
        // `legendOutside` deliberately UNSET rather than false: with the stats box gone
        // the gutter is free, and the kit derives the placement from the deposit's shape
        // (dashboard-kit's `legendTakesGutter`) — this deposit is elongated N-S, so the
        // legend takes the gutter and the plot gets the height back. Setting the flag
        // either way here would override that and pin one composition for every
        // deposit.
        groupOrder: CUTOFF_MAP_DRAW_ORDER, // lastre backdrop → mineral on top
        legendOrder: CUTOFF_MAP_LEGEND_ORDER, // …but the legend reads Q1..Q4
        // No stats box: the bench names the subtitle and its block count + accuracy
        // head the legend (dashboard-kit's shared/levelView). The box was a third
        // surface saying the same thing, and its gutter — reserved by shrinking
        // `xaxis.domain` — is what forced `constrain: 'range'` on the axes, so the
        // frame moved with every change in available width. Without it the plan view
        // keeps `constrain: 'domain'`: measured, the X range is now identical across
        // benches and across window widths.
        markerSymbol: 'square', // imitate the 20×20 m block cell
        markerSize: 7,
        // 7px is the CAP, not the size: a fixed px square on a dense bench outgrew its
        // on-screen cell and covered the neighbouring blocks (Bug 2720). The kit fits
        // each square to the cell, shrinking only where the grid is denser than 7px.
        fitMarkerToCell: true,
        showLegendCounts: true, // "Q1 … — 26" per quadrant, for the shown bench
        accuracyClasses: CUTOFF_MAP_ACCURACY_CLASSES, // legend heading accuracy = Q1+Q4
        colSpan: 2, // full-width row: room for the plot + side slider + legend
      },
    },
  ],
  map_accuracy_category: [
    {
      layout: 'spatial-map',
      idSuffix: 'map-category',
      type: 'spatialCategorical',
      // Same plan-view confusion matrix as the cut-off map, keyed on category
      // membership instead of ore/waste.
      title: 'Mapa de exactitud CP vs LP (categoría)',
      field: 'mapCategory',
      // i/j/k are grid indices; the builder turns them into real centroids
      // (i→Este/X, j→Norte/Y, k→elevation/Z) when the reader grid resolves,
      // and falls back to i/j labels otherwise.
      xKey: 'i',
      yKey: 'j',
      levelKey: 'k',
      groupKey: 'class',
      classLabels: CATEGORY_MAP_CLASS_LABELS,
      groupColors: CATEGORY_MAP_GROUP_COLORS,
      xAxisLabel: 'Este (m)',
      yAxisLabel: 'Norte (m)',
      options: {
        showLegend: true,
        showTooltip: true,
        equalAspect: true,
        levelSelector: true,
        levelSelectorLabel: 'Elevación (m)',
        levelSelectorUnit: 'm', // real centroid coord; dropped to index if grid fails
        groupOrder: CATEGORY_MAP_DRAW_ORDER,
        legendOrder: CATEGORY_MAP_LEGEND_ORDER,
        // No stats box, for the same reason as the cut-off map above: the bench goes
        // to the subtitle and its figures to the legend heading, which is what lets
        // the plan view keep `constrain: 'domain'` and a frame that holds still.
        markerSymbol: 'square',
        markerSize: 7,
        fitMarkerToCell: true, // same cap-to-cell as the cut-off map (Bug 2720)
        showLegendCounts: true,
        accuracyClasses: CATEGORY_MAP_ACCURACY_CLASSES,
        colSpan: 2,
      },
    },
  ],
  accuracy_cutoff: [
    {
      layout: 'indexed-vectors',
      idSuffix: 'accuracy-cutoff',
      type: 'line',
      title: 'Porcentaje de acierto por ley de corte (CP vs LP)',
      xKey: 'cutoff',
      xAxisLabel: 'Ley de corte (%)',
      // Lib returns 100 * matchingBlocks / totalBlocks (AlgoritmoAccuracyCutOff.cpp) → %.
      yAxisLabel: 'Acierto (%)',
      comparedColumns: ['variable_comparison_column_1', 'variable_comparison_column_2'],
      // accuracyCutOff[i] pairs with unique_cutOff[i] → plot over the real cut-off
      // values, not the 1-based index.
      xParam: 'unique_cutOff',
      sortByX: true,
      series: [
        {
          field: 'accuracyCutOff',
          label: 'Acierto',
          color: ACCURACY_LINE_COLOR,
          markers: true,
          smooth: true,
        },
      ],
      // Styled like the tonnage/grade/metal chart (US 2461): a full grid
      // (showVerticalGrid mirrors the existing horizontal-only showGrid), a
      // Y axis fit to the data instead of forced to a 0 floor — the match
      // percentage rarely approaches 0, so a 0 floor wasted most of the plot's
      // vertical scale on a region with no data (see ChartOptions.yFitData) —
      // and the same marker size as that chart (US 2446 sized its dots down
      // from the kit default 8px to 5px; matching that here, not the default,
      // is what "same size" (US 2461) means after that later change).
      options: {
        showGrid: true,
        showTooltip: true,
        showVerticalGrid: true,
        yFitData: true,
        markerSize: 5,
        // Plotly's spline bulged above 100 % where the curve leaves a 100 %
        // plateau (Bug 2764); a monotone curve never passes the data.
        smoothMonotone: true,
      },
    },
  ],
  // The Lib returns the error of ley/tonelaje/metal per category in three bases:
  // LP and CP are relative to each model (RSE_LP / RSE_CP), SP is mean-relative
  // (MSRE — it divides by the average of both). One line chart per base, each with
  // the three measures; X = the entered categories. resultToDashboard keeps the
  // ones the user ticked (`error_bases`, default a single curve) — with small
  // deviations the three differ by hundredths of a point and stack into one line.
  //
  // The acronyms are the legacy conciliatron's own, which is what the client's
  // historical reports carry, so they travel in the titles and axes to keep a new
  // figure comparable with an old report. The letter→acronym mapping is explicit in
  // `modulosPy/figuresAnalytics/byperiodMovErrorGtm.py` ('L' → RSE_LP, 'C' → RSE_CP,
  // 'B' → MSRE); `modulosPy/betaFactors/errors_functions.py` names the same three
  // formulas (`calc_error_RSE_LP` / `_RSE_CP` / `_MSRE`). `errorPorTipo`
  // (`modulosPy/figureDataCalculos.py`) is where the formulas live, but it dispatches
  // on the bare letters — it is not the source of the acronyms.
  //
  // The note states how the figure aggregates, because a reader of a printed report
  // cannot infer it from the axes: the Lib totals each category first
  // (ley media = ΣMetal/ΣTonelaje) and takes the error of those totals.
  //
  // The legacy did the same thing structurally — `modulosPy/figureDataRegroupData2.py`
  // holds 90 of the 94 `errorPorTipo` call sites and aggregates (`promGrupo` /
  // `sumaGrupo`) before computing the error once. So the note is not a contrast with
  // the legacy; the per-block-then-`np.nanmean` path exists only in the moving-window
  // and by-period figures. Where the two genuinely differ is the WEIGHT: the legacy
  // averages the grade by block count (`nCoexSuma`), the Lib by tonnage. They agree
  // under a global density and diverge with a density column.
  error: [
    {
      layout: 'indexed-vectors',
      idSuffix: 'error-lp',
      toleranceLines: true,
      type: 'line',
      title: 'Error relativo de reconciliación — base LP (RSE_LP)',
      xKey: 'categoria',
      xParam: 'unique_categories',
      xAxisLabel: 'Categoría',
      yAxisLabel: 'RSE_LP (%)',
      subtitleNote: NOTA_METODO_ERROR,
      comparedColumns: ['value_columns_1', 'value_columns_2'],
      groupColumn: ['category_column_1', 'category_column_2'],
      series: [
        {
          field: 'oreGradeErrorLPByCategories',
          label: 'Ley',
          preScaledWhen: 'is_accumulative_error',
          ...ERROR_CURVE,
        },
        { field: 'tonnageErrorLPByCategories', label: 'Tonelaje', ...ERROR_CURVE },
        { field: 'metalErrorLPByCategories', label: 'Metal', ...ERROR_CURVE },
      ],
      options: {
        showLegend: true,
        showGrid: true,
        showTooltip: true,
        xAxisCategorical: true,
        markerSize: TONNAGE_MARKER_SIZE, // same dots as the tonnage chart (US 2510)
      },
    },
    {
      layout: 'indexed-vectors',
      idSuffix: 'error-cp',
      toleranceLines: true,
      type: 'line',
      title: 'Error relativo de reconciliación — base CP (RSE_CP)',
      xKey: 'categoria',
      xParam: 'unique_categories',
      xAxisLabel: 'Categoría',
      yAxisLabel: 'RSE_CP (%)',
      subtitleNote: NOTA_METODO_ERROR,
      comparedColumns: ['value_columns_1', 'value_columns_2'],
      groupColumn: ['category_column_1', 'category_column_2'],
      series: [
        {
          field: 'oreGradeErrorCPByCategories',
          label: 'Ley',
          preScaledWhen: 'is_accumulative_error',
          ...ERROR_CURVE,
        },
        { field: 'tonnageErrorCPByCategories', label: 'Tonelaje', ...ERROR_CURVE },
        { field: 'metalErrorCPByCategories', label: 'Metal', ...ERROR_CURVE },
      ],
      options: {
        showLegend: true,
        showGrid: true,
        showTooltip: true,
        xAxisCategorical: true,
        markerSize: TONNAGE_MARKER_SIZE, // same dots as the tonnage chart (US 2510)
      },
    },
    {
      layout: 'indexed-vectors',
      idSuffix: 'error-sp',
      toleranceLines: true,
      type: 'line',
      title: 'Error simétrico de reconciliación (MSRE)',
      xKey: 'categoria',
      xParam: 'unique_categories',
      xAxisLabel: 'Categoría',
      yAxisLabel: 'MSRE (%)',
      subtitleNote: NOTA_METODO_ERROR,
      comparedColumns: ['value_columns_1', 'value_columns_2'],
      groupColumn: ['category_column_1', 'category_column_2'],
      series: [
        {
          field: 'oreGradeErrorSPByCategories',
          label: 'Ley',
          preScaledWhen: 'is_accumulative_error',
          ...ERROR_CURVE,
        },
        { field: 'tonnageErrorSPByCategories', label: 'Tonelaje', ...ERROR_CURVE },
        { field: 'metalErrorSPByCategories', label: 'Metal', ...ERROR_CURVE },
      ],
      options: {
        showLegend: true,
        showGrid: true,
        showTooltip: true,
        xAxisCategorical: true,
        markerSize: TONNAGE_MARKER_SIZE, // same dots as the tonnage chart (US 2510)
      },
    },
  ],
  match_geology: [
    {
      // matchCategories[i] = % of blocks (where both models share geology
      // category i) that also agree on the comparison attribute → one bar per
      // category, x labelled by the entered `unique_categories`.
      layout: 'indexed-vectors',
      idSuffix: 'match-geology',
      type: 'bar',
      title: 'Acierto geológico por categoría (CP vs LP)',
      xKey: 'categoria',
      xAxisLabel: 'Tipo de roca',
      yAxisLabel: 'Acierto (%)',
      xParam: 'unique_categories',
      // The compared attribute is the geology category; the grouping (x) column is
      // the rock-type column the accuracy is broken down by.
      comparedColumns: ['category_comparison_column_1', 'category_comparison_column_2'],
      groupColumn: ['category_column_1', 'category_column_2'],
      series: [{ field: 'matchCategories', label: 'Acierto' }],
      options: { showGrid: true, showTooltip: true, xAxisCategorical: true },
    },
  ],
};

/**
 * Algorithms intentionally without a chart — fall back to raw JSON. Empty:
 * all nine algorithms map to a default chart.
 */
export const RAW_JSON_ONLY = new Set<string>([]);

/** Every output field a list of chart intents references (for drift-guarding). */
export function chartIntentFields(charts: ChartIntent[]): string[] {
  const fields: string[] = [];
  for (const c of charts) {
    switch (c.layout) {
      case 'paired-arrays':
        fields.push(c.xField);
        if (c.xFallback) fields.push(c.xFallback);
        for (const s of c.series) fields.push(s.field);
        for (const st of c.stats ?? []) fields.push(st.field);
        break;
      case 'models-dual-axis':
        fields.push(c.primary.field, c.secondary.field);
        break;
      case 'xy-points':
      case 'spatial-map':
        fields.push(c.field);
        break;
      case 'indexed-vectors':
        for (const s of c.series) fields.push(s.field);
        break;
    }
  }
  return fields;
}
